import { useCallback, useEffect, useRef, useState } from "react";
import { AlertCircle, BarChart3 } from "lucide-react";
import styles from "../../FinancePage.module.css";
import { financeService, type FinanceDropiBackfillResponse, type FinanceMetaBackfillResponse } from "../../services/financeService";
import { useFinanceStore } from "../../store/financeStore";

interface StepProps {
  onDone: () => void;
}

const ACTIVATION_MESSAGES = [
  "Estamos aprendiendo qué campañas te están dando dinero.",
  "Calculando cuánto te queda en el bolsillo por cada venta.",
  "Encontrando tu costo de cada venta máximo rentable.",
  "Detectando dónde se está yendo tu dinero sin que te des cuenta.",
  "Casi listo. Tu negocio en números claros está por aparecer.",
  "Conectando tus ventas reales con tu inversión publicitaria.",
  "Buscando los embudos que más rentabilidad te están dando.",
  "Identificando los productos que de verdad están funcionando.",
  "Mapeando el viaje completo desde el anuncio hasta la venta.",
  "Pronto sabrás a qué precios deberías vender cada producto.",
  "Encontrando los patrones que tus mejores campañas siguen.",
  "Descubriendo cuáles campañas vale la pena escalar.",
  "Cruzando tus gastos publicitarios con tus ventas reales.",
  "Preparando tu lente financiero para tomar mejores decisiones.",
];

const MESSAGE_ROTATION_MS = 4000;
const REDIRECT_AFTER_SUCCESS_MS = 2800;

export const Step4Activating = ({ onDone }: StepProps) => {
  const [messageIndex, setMessageIndex] = useState(0);
  const [messageVisible, setMessageVisible] = useState(true);
  const [status, setStatus] = useState<"pending" | "success" | "error">("pending");
  const [activationSummary, setActivationSummary] = useState<string | null>(null);
  const [localActivationError, setLocalActivationError] = useState<string | null>(null);
  const hasStartedRef = useRef(false);
  const redirectTimeoutRef = useRef<number | null>(null);

  const activationError = useFinanceStore((s) => s.activationError);

  const startBackfill = useCallback(async () => {
    if (redirectTimeoutRef.current) {
      window.clearTimeout(redirectTimeoutRef.current);
      redirectTimeoutRef.current = null;
    }

    setStatus("pending");
    setMessageIndex(0);
    setMessageVisible(true);
    setActivationSummary(null);
    setLocalActivationError(null);

    try {
      const metaResult = await financeService.startMetaBackfill();
      const dropiResult = await financeService.startDropiBackfill().catch(() => null);
      setActivationSummary(buildActivationSummary(metaResult, dropiResult));
      setStatus("success");
      redirectTimeoutRef.current = window.setTimeout(async () => {
        try {
          await onDone();
        } catch {
          // El store ya seteó activationError. Flippeamos el status local
          // para que el render muestre el error.
          setStatus("error");
        }
      }, REDIRECT_AFTER_SUCCESS_MS);
    } catch (error) {
      setLocalActivationError(resolveActivationErrorMessage(error));
      setStatus("error");
    }
  }, [onDone]);

  useEffect(() => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;
    startBackfill();
  }, [startBackfill]);

  useEffect(() => {
    if (status !== "pending") return;

    let fadeTimeout: number | null = null;
    const interval = window.setInterval(() => {
      setMessageVisible(false);
      fadeTimeout = window.setTimeout(() => {
        setMessageIndex((current) => (current + 1) % ACTIVATION_MESSAGES.length);
        setMessageVisible(true);
      }, 260);
    }, MESSAGE_ROTATION_MS);

    return () => {
      window.clearInterval(interval);
      if (fadeTimeout) window.clearTimeout(fadeTimeout);
    };
  }, [status]);

  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) window.clearTimeout(redirectTimeoutRef.current);
    };
  }, []);

  const handleRetry = () => {
    setStatus("pending");
    startBackfill();
  };

  return (
    <div className={styles.activationScene}>
      <div className={styles.activationIcon} aria-hidden="true">
        <BarChart3 size={74} />
      </div>

      <div className={`${styles.onboardingCard} ${styles.center} ${styles.activationCard}`}>
        <h2 className={styles.onboardingTitle}>
          {status === "success" ? (
            <>
              ¡Listo! <span className={styles.accent}>Tu Fluxi Finance</span> está activo
            </>
          ) : (
            <>
              Estamos activando tu <span className={styles.accent}>Fluxi Finance</span>
            </>
          )}
        </h2>
        <p className={styles.onboardingSubCentered}>
          {status === "success"
            ? activationSummary ?? "Te llevamos al dashboard..."
            : "No te preocupes. Esto puede tomar entre 1 y 3 minutos."}
        </p>

        {status === "pending" || status === "success" ? (
          <>
            <div
              className={`${styles.activationProgress} ${status === "success" ? styles.activationProgressComplete : ""
                }`}
              aria-label="Activando Fluxi Finance"
            >
              <span />
            </div>
            {status === "pending" ? (
              <p
                className={`${styles.activationMessage} ${messageVisible ? styles.activationMessageVisible : ""
                  }`}
              >
                {ACTIVATION_MESSAGES[messageIndex]}
              </p>
            ) : null}
          </>
        ) : (
          <div className={styles.activationError}>
            <AlertCircle size={22} />
            <p>{activationError ?? localActivationError ?? "Hubo un problema al activar Fluxi Finance. Intenta de nuevo."}</p>
            <button className={styles.primaryButton} type="button" onClick={handleRetry}>
              Reintentar
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const buildActivationSummary = (
  metaResult: FinanceMetaBackfillResponse,
  dropiResult: FinanceDropiBackfillResponse | null
): string => {
  const autoMatch = metaResult.auto_match ?? metaResult.autoMatch ?? null;
  const funnelsDetected = autoMatch?.funnels_detected ?? autoMatch?.funnelsDetected ?? 0;
  const metaFunnelsMatched = autoMatch?.meta_funnels_matched ?? autoMatch?.metaFunnelsMatched ?? 0;
  const pendingFunnels = autoMatch?.pending_funnels ?? autoMatch?.pendingFunnels ?? 0;
  const dropiMatched = dropiResult?.matched_orders_count ?? dropiResult?.matchedOrdersCount ?? 0;

  const pieces: string[] = [];

  if (funnelsDetected > 0) {
    pieces.push(
      metaFunnelsMatched > 0
        ? `Conectamos ${metaFunnelsMatched} de ${funnelsDetected} embudos con tus campañas.`
        : `Detectamos ${funnelsDetected} embudos; los que queden pendientes podrás conectarlos manualmente.`
    );
  }

  if (pendingFunnels > 0 && metaFunnelsMatched > 0) {
    pieces.push(`${pendingFunnels} quedan pendientes de revisar.`);
  }

  if (dropiMatched > 0) {
    pieces.push(`Dropi ya vinculó ${dropiMatched} órdenes con embudos.`);
  }

  return pieces.length > 0 ? pieces.join(" ") : "Te llevamos al dashboard...";
};

function resolveActivationErrorMessage(error: unknown): string {
  const message = error instanceof Error ? error.message : "";
  if (message.toLowerCase().includes("network")) {
    return "No pudimos contactar el backend local. Verifica que ecommerce-service esté corriendo en localhost:8080 e intenta de nuevo.";
  }
  return "No pudimos completar la sincronización inicial con Meta. Intenta de nuevo.";
}
